import { axiosDelete, axiosGet, axiosPatch, axiosPost } from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import {
  BulkRoleMutationParamsSchema,
  RoleFormSchema,
  RoleModel,
  RolesModel,
} from '@/features/tenant-manager/models';
import { useInvalidateQuery } from '@/hooks';
import { z } from 'zod';
import { invalidateListMap, queryKeyMap } from '../utils';

const RolesModelElement = RolesModel.element;

export const useRoles = <T = z.infer<typeof RolesModelElement>>(
  select?: (data: z.infer<typeof RolesModel>) => T[],
) => {
  return useQuery({
    queryKey: queryKeyMap.roles(),
    queryFn: async () => {
      const roles = await axiosGet('/api/roles/');
      return RolesModel.parse(roles);
    },
    select,
  });
};

export const useRole = (id: string) => {
  return useQuery({
    queryKey: queryKeyMap.role(id),
    queryFn: async () => {
      const role = await axiosGet(`/api/roles/${id}/`);
      return RoleModel.parse(role);
    },
  });
};

export const useCreateRole = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (value: z.infer<typeof RoleFormSchema>) => {
      const payload = RoleFormSchema.parse(value);
      return await axiosPost('/api/roles/', payload);
    },
    onSuccess: async () => {
      await invalidate(queryKeyMap.roles());
    },
  });
};

export const useUpdateRole = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      value: z.infer<typeof RoleFormSchema> & { id: string },
    ) => {
      const { id, ...payload } = RoleFormSchema.extend({
        id: z.string().uuid(),
      }).parse(value);
      return await axiosPatch(`/api/roles/${id}/`, payload);
    },
    onSuccess: async (_, { id }) => {
      await Promise.all([
        invalidate(queryKeyMap.roles()),
        invalidate(queryKeyMap.role(id)),
      ]);
    },
  });
};

export const useDeleteRole = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (id: string) => {
      return await axiosDelete(`/api/roles/${id}/`);
    },
    onSuccess: async () => {
      await invalidate(queryKeyMap.roles());
    },
  });
};

export const useBulkRole = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (value: z.infer<typeof BulkRoleMutationParamsSchema>) => {
      const { id, organizationIds } = BulkRoleMutationParamsSchema.parse(value);
      return await axiosPost(`/api/roles/${id}/organizations/`, {
        organizationIds,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        ...invalidateListMap.organizations().map(async (invalidateList) => {
          await invalidate(invalidateList);
        }),
        invalidate(queryKeyMap.organization()),
      ]);
    },
  });
};
